// An outbox, not a history. Each queued entry is a finished record that could
// not yet be confirmed on the server, stamped with the account id it belongs
// to so a later flush under a different account discards it rather than
// mis-attributing it. Never listed, never replayed as history — deleted on
// ack, dropped wholesale on logout.
const DEFAULT_CAP = 5;

interface PendingEntry<TRecord> {
  readonly accountId: string;
  readonly record: TRecord;
}

export interface PendingSyncStoreOptions {
  readonly storageKey: string;
  readonly cap?: number;
}

export class PendingSyncStore<TRecord> {
  private readonly storageKey: string;
  private readonly cap: number;
  // Serializes flush() calls so two overlapping invocations (e.g. the boot
  // flush racing a BN_AUTH_CHANGE-triggered one) can't each snapshot load()
  // before the other's save() lands, silently dropping or resurrecting an
  // entry. Each call still runs with its own accountId; a failed run doesn't
  // block the ones queued behind it.
  private queue: Promise<void> = Promise.resolve();

  constructor(opts: PendingSyncStoreOptions) {
    this.storageKey = opts.storageKey;
    this.cap = opts.cap ?? DEFAULT_CAP;
  }

  enqueue(accountId: string, record: TRecord): void {
    const entries = [...this.load(), { accountId, record }];
    // FIFO — drop the oldest once past the cap, never refuse the newest.
    const trimmed = entries.length > this.cap ? entries.slice(entries.length - this.cap) : entries;
    this.save(trimmed);
  }

  flush(accountId: string, send: (record: TRecord) => Promise<boolean>): Promise<void> {
    const run = this.queue.then(() => this.runFlush(accountId, send));
    this.queue = run.catch(() => undefined);
    return run;
  }

  // Only entries stamped with `accountId` are sent. Entries stamped with a
  // different account are dropped, not retried — the session that could have
  // delivered them is gone (see the plan's "sign out with a queued outbox
  // record" row).
  private async runFlush(
    accountId: string,
    send: (record: TRecord) => Promise<boolean>,
  ): Promise<void> {
    const entries = this.load();
    const remaining: PendingEntry<TRecord>[] = [];
    for (const entry of entries) {
      if (entry.accountId !== accountId) continue;
      // `send` is documented to resolve, never reject, but nothing in the
      // type enforces that — a rejection here must not abort the loop before
      // save(), or every already-acked entry ahead of it gets resent next
      // flush.
      let ok: boolean;
      try {
        ok = await send(entry.record);
      } catch {
        ok = false;
      }
      if (!ok) {
        remaining.push(entry);
      }
    }
    this.save(remaining);
  }

  dropAll(): void {
    this.save([]);
  }

  private load(): PendingEntry<TRecord>[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((v): v is PendingEntry<TRecord> => this.isValid(v));
    } catch {
      return [];
    }
  }

  private isValid(value: unknown): value is PendingEntry<TRecord> {
    if (!value || typeof value !== "object") return false;
    const entry = value as Partial<PendingEntry<TRecord>>;
    return typeof entry.accountId === "string" && "record" in entry;
  }

  private save(entries: PendingEntry<TRecord>[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(entries));
    } catch {
      // Storage full / unavailable — the outbox just won't persist across reload.
    }
  }
}
